import { BarChart } from '@mantine/charts';
import {
  Alert,
  Badge,
  Box,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StatisticsHeader } from '../../components/statistics-header/statistics-header';
import { DefaultOsRecapSection } from '../../components/default-os-recap/default-os-recap';
import { dashboardQueries } from '../../data-access/queries/dashboard.queries';
import {
  DashboardClause,
  DEFAULT_GP_CAMPAIGN_YEARS,
  DEFAULT_NGP_CAMPAIGN_YEARS,
} from '../../data-access/types/stats';
import {
  useDashboardOrganisations,
  useInitializeOrganisationSelection,
} from '../../hooks/use-dashboard-organisations';
import { sameIdSets } from '../../utils/id-set-filter';
import {
  computeCompletionRate,
  formatPercent,
  getCompletionColor,
} from '../../utils/completion-metrics';
import {
  formatVolumeLabel,
  formatVolumeTonnes,
} from '../../utils/format-volume';

import { ErrorPage } from './page.error';
import { PendingPage, StatisticsSkeleton } from './page.pending';

import { Permission, usePermissions } from '@auth/data-access';
import { useQueriesWithGlobal } from '@data-access';

const resolveDefaultNgpYear = () => {
  const currentYear = new Date().getFullYear();
  if ((DEFAULT_NGP_CAMPAIGN_YEARS as readonly number[]).includes(currentYear)) {
    return currentYear;
  }
  return DEFAULT_NGP_CAMPAIGN_YEARS[DEFAULT_NGP_CAMPAIGN_YEARS.length - 1];
};

export function StatisticsPage() {
  const { t } = useTranslation();
  const { can, isAdmin, isIndustrial } = usePermissions();
  const showOwnerFilter = can(Permission.DashboardOwnerFilter);
  const showDefaultOsRecap = isAdmin || isIndustrial;
  const [organisationIds, setOrganisationIds] = useState<number[] | null>(null);
  const [ownerIds, setOwnerIds] = useState<number[] | null>(null);
  const [clause, setClause] = useState<DashboardClause>('gp');
  const [campaignYear, setCampaignYear] = useState(resolveDefaultNgpYear);
  const [gpCampaignYears, setGpCampaignYears] = useState<number[]>(() => [
    ...DEFAULT_GP_CAMPAIGN_YEARS,
  ]);
  const organisationsQuery = useDashboardOrganisations();

  useInitializeOrganisationSelection(
    organisationIds,
    setOrganisationIds,
    organisationsQuery.data,
    clause,
  );

  const selectedOrganisationIds = organisationIds ?? [];
  const isOrganisationSelectionReady = organisationIds !== null;
  const synthesisCampaignYear = clause === 'new' ? campaignYear : null;
  const synthesisOwnerIds = showOwnerFilter ? ownerIds : null;

  const queries = useQueriesWithGlobal(dashboardQueries);
  const pratiquesSynthesis = useQuery({
    ...queries.pratiquesSynthesis(
      selectedOrganisationIds,
      synthesisOwnerIds,
      clause,
      synthesisCampaignYear,
    ),
    enabled: isOrganisationSelectionReady,
  });
  const ownerOptions = pratiquesSynthesis.data?.available_owners ?? [];
  const availableCampaignYears =
    pratiquesSynthesis.data?.available_campaign_years ??
    DEFAULT_NGP_CAMPAIGN_YEARS;

  useEffect(() => {
    if (!showOwnerFilter) {
      return;
    }

    const allOwnerIds = ownerOptions.map((owner) => owner.id);
    if (allOwnerIds.length === 0) {
      return;
    }

    setOwnerIds((previous) => {
      if (previous === null) {
        return allOwnerIds;
      }
      const next = previous.filter((id) => allOwnerIds.includes(id));
      const resolved = next.length > 0 ? next : allOwnerIds;
      return sameIdSets(previous, resolved) ? previous : resolved;
    });
  }, [ownerOptions, showOwnerFilter]);

  const handleClauseChange = (nextClause: DashboardClause) => {
    setClause(nextClause);
    if (showOwnerFilter) {
      setOwnerIds(null);
    }
  };

  const campaignCards = useMemo(() => {
    if (!pratiquesSynthesis.data?.results) return [];

    return Object.entries(pratiquesSynthesis.data.results)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([campaign, speciesMap]) => {
        const speciesEntries = Object.entries(speciesMap).map(
          ([species, metrics]) => ({
            species,
            metrics,
            speciesLabel: t(`common.crops.${species}`, { defaultValue: species }),
            hasDataRate:
              metrics.pratiques_count > 0
                ? computeCompletionRate(
                    metrics.has_data_count,
                    metrics.pratiques_count,
                  )
                : 0,
            hasFieldsRate:
              metrics.pratiques_count > 0
                ? computeCompletionRate(
                    metrics.has_fields_count,
                    metrics.pratiques_count,
                  )
                : 0,
          }),
        );

        return {
          campaign,
          speciesEntries,
          totalPratiquesCount: speciesEntries.reduce(
            (sum, { metrics }) => sum + metrics.pratiques_count,
            0,
          ),
          totalVolume: speciesEntries.reduce(
            (sum, { metrics }) => sum + metrics.total_volume,
            0,
          ),
          barData: speciesEntries.map(({ speciesLabel, metrics }) => ({
            species: speciesLabel,
            total_volume: metrics.total_volume,
            has_data_volume: metrics.has_data_volume,
            has_fields_volume: metrics.has_fields_volume,
          })),
        };
      });
  }, [pratiquesSynthesis.data, t]);

  const visibleCampaignCards = useMemo(() => {
    if (clause !== 'gp') {
      return campaignCards;
    }
    const selectedYears = new Set(gpCampaignYears);
    return campaignCards.filter((card) =>
      selectedYears.has(Number(card.campaign)),
    );
  }, [campaignCards, clause, gpCampaignYears]);

  const globalRecap = useMemo(() => {
    return visibleCampaignCards.reduce(
      (acc, campaignCard) => {
        campaignCard.speciesEntries.forEach(({ metrics }) => {
          acc.pratiquesCount += metrics.pratiques_count;
          acc.totalVolume += metrics.total_volume;
          acc.hasDataCount += metrics.has_data_count;
          acc.hasFieldsCount += metrics.has_fields_count;
          acc.hasDataVolume += metrics.has_data_volume;
          acc.hasFieldsVolume += metrics.has_fields_volume;
        });

        return acc;
      },
      {
        pratiquesCount: 0,
        totalVolume: 0,
        hasDataCount: 0,
        hasFieldsCount: 0,
        hasDataVolume: 0,
        hasFieldsVolume: 0,
      },
    );
  }, [visibleCampaignCards]);

  const globalHasDataRate = computeCompletionRate(
    globalRecap.hasDataCount,
    globalRecap.pratiquesCount,
  );
  const globalHasFieldsRate = computeCompletionRate(
    globalRecap.hasFieldsCount,
    globalRecap.pratiquesCount,
  );
  const globalHasDataVolumeRate =
    globalRecap.totalVolume > 0
      ? (globalRecap.hasDataVolume / globalRecap.totalVolume) * 100
      : 0;
  const globalHasFieldsVolumeRate =
    globalRecap.totalVolume > 0
      ? (globalRecap.hasFieldsVolume / globalRecap.totalVolume) * 100
      : 0;

  return (
    <>
      <Box mb="md">
        <StatisticsHeader
          availableCampaignYears={availableCampaignYears}
          campaignYear={campaignYear}
          clause={clause}
          gpCampaignYears={gpCampaignYears}
          onCampaignYearChange={setCampaignYear}
          onClauseChange={handleClauseChange}
          onGpCampaignYearsChange={setGpCampaignYears}
          onOrganisationIdsChange={(ids) => setOrganisationIds(ids)}
          onOwnerIdsChange={setOwnerIds}
          organisationIds={selectedOrganisationIds}
          ownerIds={ownerIds ?? []}
          ownerOptions={ownerOptions}
          showOwnerFilter={showOwnerFilter}
        />
      </Box>
      <Stack mb="md">
        {pratiquesSynthesis.isPending && <StatisticsSkeleton />}
        {pratiquesSynthesis.isError && (
          <Alert color="red" title={t('common.error.title', { defaultValue: 'Error' })}>
            {t('dashboard.pratiques.cards.error', {
              defaultValue: 'Unable to load pratiques synthesis data.',
            })}
          </Alert>
        )}
        {!pratiquesSynthesis.isPending &&
          !pratiquesSynthesis.isError &&
          visibleCampaignCards.length === 0 && (
            <Paper p="md">
              <Stack gap="xs">
                <Title order={4}>
                  {t('dashboard.pratiques.cards.title', {
                    defaultValue: 'Pratiques synthesis',
                  })}
                </Title>
                <Text c="dimmed" size="sm">
                  {t('dashboard.pratiques.cards.empty', {
                    defaultValue: 'No pratiques synthesis data available.',
                  })}
                </Text>
              </Stack>
            </Paper>
          )}
        {!pratiquesSynthesis.isPending &&
          !pratiquesSynthesis.isError &&
          visibleCampaignCards.length > 0 && (
            <Paper p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3}>
                  {clause === 'new'
                    ? t('dashboard.pratiques.cards.global-recap-year', {
                        defaultValue: 'Récapitulatif {{year}}',
                        year: campaignYear,
                      })
                    : t('dashboard.pratiques.cards.global-recap', {
                        defaultValue: 'Récapitulatif global (campagnes sélectionnées)',
                      })}
                </Title>
                <SimpleGrid
                  cols={{
                    base: 1,
                    sm: 2,
                  }}
                >
                  <Paper p="sm" radius="md" withBorder>
                    <Text c="dimmed" size="xs">
                      {t('dashboard.pratiques.kpis.pratiques-count', {
                        defaultValue: 'Nombre de pratiques',
                      })}
                    </Text>
                    <Text fw={700} size="xl">
                      {globalRecap.pratiquesCount}
                    </Text>
                  </Paper>
                  <Paper p="sm" radius="md" withBorder>
                    <Text c="dimmed" size="xs">
                      {t('dashboard.pratiques.kpis.total-volume', {
                        defaultValue: 'Volume total',
                      })}
                    </Text>
                    <Text fw={700} size="xl">
                      {formatVolumeLabel(globalRecap.totalVolume)}
                    </Text>
                  </Paper>
                </SimpleGrid>
                <SimpleGrid
                  cols={{
                    base: 1,
                    md: 2,
                  }}
                >
                  <Paper p="sm" radius="md" withBorder>
                    <Stack gap={4}>
                      <Text fw={600} size="sm">
                        {t('dashboard.pratiques.kpis.has-data-rate', {
                          defaultValue: 'Données',
                        })}
                      </Text>
                      <Group gap="xs" wrap="wrap">
                        <Text size="sm">
                          <strong>
                            {globalRecap.hasDataCount} / {globalRecap.pratiquesCount}
                          </strong>{' '}
                          ({formatPercent(globalHasDataRate)})
                        </Text>
                        <Text c="dimmed" size="sm">
                          -
                        </Text>
                        <Text size="sm">
                          <strong>
                            {formatVolumeLabel(globalRecap.hasDataVolume)} /{' '}
                            {formatVolumeLabel(globalRecap.totalVolume)}
                          </strong>{' '}
                          ({formatPercent(globalHasDataVolumeRate)})
                        </Text>
                      </Group>
                    </Stack>
                    <Progress
                      color={getCompletionColor(globalHasDataRate)}
                      mt="xs"
                      size="lg"
                      value={globalHasDataRate}
                    />
                  </Paper>
                  <Paper p="sm" radius="md" withBorder>
                    <Stack gap={4}>
                      <Text fw={600} size="sm">
                        {t('dashboard.pratiques.kpis.has-fields-rate', {
                          defaultValue: 'Parcelles',
                        })}
                      </Text>
                      <Group gap="xs" wrap="wrap">
                        <Text size="sm">
                          <strong>
                            {globalRecap.hasFieldsCount} / {globalRecap.pratiquesCount}
                          </strong>{' '}
                          ({formatPercent(globalHasFieldsRate)})
                        </Text>
                        <Text c="dimmed" size="sm">
                          -
                        </Text>
                        <Text size="sm">
                          <strong>
                            {formatVolumeLabel(globalRecap.hasFieldsVolume)} /{' '}
                            {formatVolumeLabel(globalRecap.totalVolume)}
                          </strong>{' '}
                          ({formatPercent(globalHasFieldsVolumeRate)})
                        </Text>
                      </Group>
                    </Stack>
                    <Progress
                      color={getCompletionColor(globalHasFieldsRate)}
                      mt="xs"
                      size="lg"
                      value={globalHasFieldsRate}
                    />
                  </Paper>
                </SimpleGrid>
                <Text c="dimmed" size="xs">
                  {t('dashboard.pratiques.cards.global-recap-help', {
                    defaultValue:
                      'Les taux sont calculés sur le nombre de pratiques. Les volumes sont affichés en tonnes.',
                  })}
                </Text>
              </Stack>
            </Paper>
          )}
        <SimpleGrid
          cols={{
            base: 1,
            md: visibleCampaignCards.length > 1 ? 2 : 1,
          }}
        >
          {visibleCampaignCards.map((campaignCard) => (
            <Paper key={campaignCard.campaign} p="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <Badge size="lg" variant="light">
                    {t('common.campaign')} {campaignCard.campaign}
                  </Badge>
                  <Group gap="md">
                    <Text size="sm">
                      {t('dashboard.pratiques.kpis.pratiques-count', {
                        defaultValue: 'Nombre de pratiques',
                      })}
                      : <strong>{campaignCard.totalPratiquesCount}</strong>
                    </Text>
                    <Text size="sm">
                      {t('dashboard.pratiques.kpis.total-volume', {
                        defaultValue: 'Volume total',
                      })}
                      : <strong>{formatVolumeLabel(campaignCard.totalVolume)}</strong>
                    </Text>
                  </Group>
                </Group>
                <SimpleGrid
                  cols={{
                    base: 1,
                    sm: visibleCampaignCards.length === 1 ? 2 : 1,
                    md: visibleCampaignCards.length === 1 ? 4 : 2,
                    xl: visibleCampaignCards.length === 1 ? 5 : 2,
                  }}
                >
                  {campaignCard.speciesEntries.map(
                    ({ species, speciesLabel, metrics, hasDataRate, hasFieldsRate }) => (
                      <Paper key={`${campaignCard.campaign}-${species}`} p="sm" withBorder>
                        <Stack gap="xs">
                          <Text fw={600}>{speciesLabel}</Text>
                          <Group grow>
                            <Text size="sm">
                              {t('dashboard.pratiques.kpis.pratiques-count', {
                                defaultValue: 'Nombre de pratiques',
                              })}
                              : <strong>{metrics.pratiques_count}</strong>
                            </Text>
                            <Text size="sm">
                              {t('dashboard.pratiques.kpis.total-volume', {
                                defaultValue: 'Total volume',
                              })}
                              : <strong>{formatVolumeLabel(metrics.total_volume)}</strong>
                            </Text>
                          </Group>
                          <Stack gap={2}>
                            <Text fw={600} size="sm">
                              {t('dashboard.pratiques.kpis.has-data-rate', {
                                defaultValue: 'Data completion rate',
                              })}
                            </Text>
                            <Group gap="xs" wrap="wrap">
                              <Text size="sm">
                                <strong>
                                  {metrics.has_data_count} / {metrics.pratiques_count}
                                </strong>{' '}
                                ({formatPercent(hasDataRate)})
                              </Text>
                              <Text c="dimmed" size="sm">
                                -
                              </Text>
                              <Text size="sm">
                                <strong>
                                  {formatVolumeLabel(metrics.has_data_volume)} /{' '}
                                  {formatVolumeLabel(metrics.total_volume)}
                                </strong>{' '}
                                (
                                {formatPercent(
                                  metrics.total_volume > 0
                                    ? (metrics.has_data_volume / metrics.total_volume) * 100
                                    : 0,
                                )}
                                )
                              </Text>
                            </Group>
                          </Stack>
                          <Progress
                            color={getCompletionColor(hasDataRate)}
                            size="lg"
                            value={hasDataRate}
                          />
                          <Stack gap={2}>
                            <Text fw={600} size="sm">
                              {t('dashboard.pratiques.kpis.has-fields-rate', {
                                defaultValue: 'Fields completion rate',
                              })}
                            </Text>
                            <Group gap="xs" wrap="wrap">
                              <Text size="sm">
                                <strong>
                                  {metrics.has_fields_count} / {metrics.pratiques_count}
                                </strong>{' '}
                                ({formatPercent(hasFieldsRate)})
                              </Text>
                              <Text c="dimmed" size="sm">
                                -
                              </Text>
                              <Text size="sm">
                                <strong>
                                  {formatVolumeLabel(metrics.has_fields_volume)} /{' '}
                                  {formatVolumeLabel(metrics.total_volume)}
                                </strong>{' '}
                                (
                                {formatPercent(
                                  metrics.total_volume > 0
                                    ? (metrics.has_fields_volume / metrics.total_volume) * 100
                                    : 0,
                                )}
                                )
                              </Text>
                            </Group>
                          </Stack>
                          <Progress
                            color={getCompletionColor(hasFieldsRate)}
                            size="lg"
                            value={hasFieldsRate}
                          />
                        </Stack>
                      </Paper>
                    ),
                  )}
                </SimpleGrid>
                <BarChart
                  data={campaignCard.barData}
                  dataKey="species"
                  h={240}
                  series={[
                    {
                      name: 'total_volume',
                      label: t('dashboard.pratiques.graph.total-volume', {
                        defaultValue: 'Total volume',
                      }),
                      color: 'gray.5',
                    },
                    {
                      name: 'has_data_volume',
                      label: t('dashboard.pratiques.graph.has-data-volume', {
                        defaultValue: 'Volume with data',
                      }),
                      color: 'blue.5',
                    },
                    {
                      name: 'has_fields_volume',
                      label: t('dashboard.pratiques.graph.has-fields-volume', {
                        defaultValue: 'Volume with fields',
                      }),
                      color: 'teal.5',
                    },
                  ]}
                  tickLine="xy"
                  unit=" T"
                  valueFormatter={(value) => formatVolumeTonnes(value, true)}
                  withLegend
                />
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
        {showDefaultOsRecap ? (
          <DefaultOsRecapSection
            campaignYear={synthesisCampaignYear}
            clause={clause}
            ownerIds={synthesisOwnerIds}
            showOwnerFilter={showOwnerFilter}
          />
        ) : null}
      </Stack>
    </>
  );
}

StatisticsPage.Error = ErrorPage;
StatisticsPage.Pending = PendingPage;

export default StatisticsPage;
